/**
 * Pendo — in-app announcements and feedback surveys.
 *
 * Pendo is browser-only. It never touches Postgres and never calls our API; it receives exactly
 * the identity fields passed to `initialize()` below and nothing else. Announcements and surveys
 * are authored in Pendo's own designer, so adding one needs no code change and no deploy — this
 * file exists solely to load the agent and tell it who the signed-in person is.
 *
 * Two things here are deliberate and easy to get wrong:
 *
 * 1. `initialize()` is NOT called at page load, unlike the copy-paste snippet on Pendo's install
 *    page. At page load the SPA does not know who the user is yet -- identity only exists after
 *    the router guard awaits `session.load()` and `GET /api/me` resolves. Initializing before
 *    then would register a visitor with an undefined id, and Pendo cannot repair that later: if
 *    an id mapping changes after implementation, Pendo creates a NEW visitor record with no
 *    product-usage or guide-view history rather than renaming the old one. Guide "show once"
 *    state lives on the visitor record, so a botched id also silently breaks announcement
 *    suppression. The stub queue installed by `installStub()` is what makes deferring safe:
 *    calls made before pendo.js finishes downloading are replayed once it loads.
 *
 * 2. Nothing in here may throw into its caller. It runs on the sign-in path, and an analytics
 *    failure must never be able to block a sign-in.
 *
 * 3. Dev and prod share ONE Pendo app, which is Pendo's own recommendation for two environments
 *    of the same product: a guide is built once, left in the Staged state so it appears only on
 *    the domain registered under Settings > Staging (dev), and promoted by flipping it to
 *    Public. Separate apps would mean rebuilding every guide, since guides do not move between
 *    apps. The price of sharing one app is that ids must not collide -- see ID_PREFIX.
 */
import type { Me } from "./api";

/** Pendo's `publicAppId`. Not a secret -- it ships in the bundle and is visible in page source,
 * which is by design for a browser agent. It lives in an env var only so an environment can be
 * pointed at a different Pendo app without a code change. Unset (the local default) disables
 * Pendo, and is what keeps prod inert today. Dev and prod set the SAME key -- see note 3.
 *
 * The key must belong to BEP's INTERNAL Pendo subscription. The first key shipped here belonged
 * to the customer-facing one, whose subscription-wide exclude rule matches
 * "@buyersedgeplatform.com" so that staff browsing a customer product do not skew its numbers.
 * Velma has no users who are not staff, so that rule silently discarded all of them: the agent
 * loaded, the requests left the browser, and the dashboard showed nothing. Nothing errors in that
 * failure mode, so check the subscription first if Pendo ever goes quiet again. */
const API_KEY: string = import.meta.env.VITE_PENDO_API_KEY ?? "";

/** Namespaces this environment's visitor and account ids. "dev-" on dev, empty in prod.
 *
 * Load-bearing rather than cosmetic, because dev and prod share one app key (note 3). Pendo is
 * explicit that visitor and account ids must be unique per api key, and that excluding an
 * environment does NOT address id collisions -- an exclude list keeps dev out of the numbers but
 * does not give dev its own visitor records. Guide state is tracked per visitor id, so a shared
 * id would mean dismissing an announcement on dev also suppresses it on prod for that person,
 * which is the exact behaviour announcements exist to get right. Accounts are prefixed for the
 * same reason. `visitor.email` deliberately is not: see below. */
const ID_PREFIX: string = import.meta.env.VITE_PENDO_ID_PREFIX ?? "";

/** Mirrors `NO_EMAIL_BRAND` in rag/user_directory.py so a Pendo account id always matches the
 * brand string the Users page filters on. */
const NO_EMAIL_BRAND = "(no email)";

/** The agent surface Pendo's own loader stubs out. Kept to what we call, plus the index signature
 * the stub needs to assign methods by name. */
interface PendoAgent {
  _q?: unknown[][];
  initialize(options: Record<string, unknown>): void;
  identify(options: Record<string, unknown>): void;
  [key: string]: unknown;
}

declare global {
  interface Window {
    pendo?: PendoAgent;
  }
}

/** The methods Pendo's install snippet queues before the real agent arrives. `initialize` is
 * unshifted rather than pushed so it always replays first, whatever order callers used. */
const AGENT_METHODS = [
  "initialize",
  "identify",
  "updateOptions",
  "pageLoad",
  "track",
  "trackAgent",
] as const;

let agentRequested = false;
let initialized = false;

/**
 * The brand someone belongs to, taken from the domain after the "@".
 *
 * A direct port of `brand_of()` in rag/user_directory.py -- BEP is a group of acquired brands on
 * separate mail domains, so the domain is the closest thing to a brand field that exists. Keep
 * the two in step: this value is the Pendo account id, and divergence would split one brand's
 * visitors across two accounts.
 */
export function brandOf(email: string | null | undefined): string {
  if (!email || !email.includes("@")) return NO_EMAIL_BRAND;
  const domain = email.slice(email.lastIndexOf("@") + 1).trim().toLowerCase();
  return domain || NO_EMAIL_BRAND;
}

/** Pendo's stub agent: queues calls until pendo.js loads and drains them. */
function installStub(): PendoAgent {
  const existing = window.pendo;
  if (existing) return existing;
  const agent: PendoAgent = {
    _q: [],
    initialize() {},
    identify() {},
  };
  for (const method of AGENT_METHODS) {
    agent[method] = (...args: unknown[]) => {
      const entry: unknown[] = [method, ...args];
      if (method === "initialize") agent._q?.unshift(entry);
      else agent._q?.push(entry);
    };
  }
  window.pendo = agent;
  return agent;
}

function requestAgent(apiKey: string): void {
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
  const firstScript = document.getElementsByTagName("script")[0];
  if (firstScript?.parentNode) firstScript.parentNode.insertBefore(script, firstScript);
  else document.head.appendChild(script);
}

/**
 * Tell Pendo who is signed in. Safe to call on every `/api/me` resolution: the first call
 * initializes, later ones re-identify (Pendo throws if initialized twice).
 *
 * Call sites should not need to guard -- this no-ops when Pendo is disabled or the caller is not
 * someone we want a visitor record for, and never throws.
 */
export function identifyPendo(me: Me | null): void {
  try {
    // No key: Pendo is off for this build. The local default, and prod until its secret is set.
    if (!API_KEY) return;
    // No email means no stable id, and a bad id is unfixable -- see note 1 at the top.
    if (!me?.email) return;
    // Dev-lockdown rejects: the router bounces them to /dev-blocked, so they are not real users
    // and should not become visitor records or consume guide impressions.
    if (me.dev_allowed === false) return;

    // Lower-cased to match how the backend keys identity (`_role_key` in rag/user_directory.py).
    // The id must be byte-stable forever, so casing drift from the token cannot be tolerated.
    const email = me.email.trim().toLowerCase();

    const options = {
      visitor: {
        // Prefixed on dev so the two environments cannot end up sharing one visitor record. That
        // makes this a namespaced key that merely looks like an address, not a mailbox -- the
        // real one is the `email` field below.
        id: ID_PREFIX + email,
        // Pendo's reserved field for NPS/Feedback delivery, so this stays the raw address even on
        // dev, where prefixing it would only produce mail that bounces. It discloses nothing the
        // id does not already. `full_name` is deliberately omitted: it would be new data leaving
        // the browser, and no guide needs it yet.
        email,
        is_admin: me.is_admin,
        is_manager: me.is_manager,
        // Set while an Admin is viewing as a Manager. The id above stays the real admin (/api/me
        // returns the token's own email and reports the impersonation target separately), so this
        // is what lets View-as sessions be filtered out of analytics instead of skewing them.
        acting_as: me.acting_as ?? "",
      },
      // Prefixed alongside the visitor id, on Pendo's same instruction that account ids be unique
      // per app key. Without it, dev traffic would land in prod's brand account.
      account: { id: ID_PREFIX + brandOf(email) },
    };

    const agent = installStub();
    if (!agentRequested) {
      requestAgent(API_KEY);
      agentRequested = true;
    }
    if (initialized) {
      agent.identify(options);
    } else {
      agent.initialize(options);
      initialized = true;
    }
  } catch (e) {
    // Warn rather than swallow -- this is how a broken install shows up during dev validation --
    // but never rethrow: see note 2 at the top.
    console.warn("Pendo initialization skipped:", e);
  }
}
